import { FullScreenIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { BADGE_CLASS } from '../../sop-workflow/util';
import {
  TimelineEntryViewProps,
  useTimelineEntryView,
} from './useTimelineEntryView';

const LABEL_CLASS =
  'text-onlight-text-2 dark:text-ondark-text-2 text-xs font-medium uppercase tracking-wide';

export function DispatchTimelineEntryViewWide({
  ev,
  pending: p,
  pendingFiles = [],
  timezone: tz,
  editDisabled,
  onEdit,
}: TimelineEntryViewProps) {
  const {
    effectiveType,
    badgeLabel,
    effectiveComment,
    allSnapshotUrls,
    snapshotCount,
  } = useTimelineEntryView(ev, p, pendingFiles, tz);
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);

  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-sm font-medium ${BADGE_CLASS[effectiveType]}`}
        >
          {badgeLabel}
        </span>
        {!editDisabled && (
          <Button
            variant="icon"
            className="ml-auto shrink-0 !p-1"
            onClick={onEdit}
          >
            <PencilIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {(snapshotCount > 0 || !!effectiveComment) && (
        <div className="space-y-3 pt-2">
          {effectiveComment && (
            <div className="space-y-1">
              <div className={LABEL_CLASS}>Comment</div>
              <p className="text-onlight-text-1 dark:text-ondark-text-1 text-sm">
                {effectiveComment}
              </p>
            </div>
          )}
          {snapshotCount > 0 && (
            <div className="space-y-1.5">
              <div className={LABEL_CLASS}>Snapshots ({snapshotCount})</div>
              <div className="flex flex-wrap items-center gap-3">
                {allSnapshotUrls.slice(0, 3).map((url, i) => (
                  <div key={i} className="group relative shrink-0">
                    <img
                      src={url}
                      alt={`Snapshot ${i + 1}`}
                      className="h-28 w-48 cursor-pointer rounded object-cover"
                      onClick={() => setPreviewIdx(i)}
                    />
                    <Button
                      variant="icon"
                      className="absolute right-1 top-1 bg-black/50 !p-0.5 opacity-0 transition-opacity hover:bg-black/75 group-hover:opacity-100"
                      onClick={() => setPreviewIdx(i)}
                    >
                      <FullScreenIcon className="fill-onlight-text-1 dark:fill-ondark-text-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                {snapshotCount > 3 && (
                  <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
                    +{snapshotCount - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <Modal
        open={previewIdx !== null}
        onClose={() => setPreviewIdx(null)}
        title={
          previewIdx !== null
            ? `Snapshot ${previewIdx + 1} of ${allSnapshotUrls.length}`
            : ''
        }
        closable
      >
        <div className="w-full max-w-4xl p-2">
          <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
            {previewIdx !== null && (
              <img
                src={allSnapshotUrls[previewIdx]}
                alt={`Preview ${previewIdx + 1}`}
                className="h-full w-full object-contain"
              />
            )}
            {allSnapshotUrls.length > 1 && previewIdx !== null && (
              <Button
                variant="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 !p-1.5 hover:bg-black/75"
                onClick={() =>
                  setPreviewIdx(
                    (previewIdx - 1 + allSnapshotUrls.length) %
                      allSnapshotUrls.length
                  )
                }
              >
                <ChevronLeftIcon className="h-5 w-5 text-white" />
              </Button>
            )}
            {allSnapshotUrls.length > 1 && previewIdx !== null && (
              <Button
                variant="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 !p-1.5 hover:bg-black/75"
                onClick={() =>
                  setPreviewIdx((previewIdx + 1) % allSnapshotUrls.length)
                }
              >
                <ChevronRightIcon className="h-5 w-5 text-white" />
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
